import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { FilterCondition, RealtimeAlertLevel, RealtimeFeed, RealtimeSearchRequest } from '../../services/api';
import { REALTIME_ALERT_LABELS, REALTIME_ALERT_LEVELS } from '../../features/realtime/config';
import { FieldDefinition } from '../../utils/filterUtils';
import { DEFAULT_CONDITION_FILTER, normalizeConditionFilters } from '../../utils/conditionSearchState';
import { MAX_CONDITIONS, validateConditions } from '../../utils/conditionSearchFilters';
import CustomSelect from '../CustomSelect';
import SearchConditionEditor from '../search-workspace/SearchConditionEditor';
import SearchConfigurationPanel from '../search-workspace/SearchConfigurationPanel';
import SearchQueryInput from '../search-workspace/SearchQueryInput';
import './RealtimeSearchPanel.css';

export interface RealtimeSearchPanelProps {
  fields: FieldDefinition[];
  feeds: Pick<RealtimeFeed, 'feed_id' | 'name'>[];
  fixedFeedId?: string;
  onSearch: (request: RealtimeSearchRequest) => void;
  onClear: () => void;
  searching: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onFeedScopeChange?: (feedId: string) => void;
  initialRequest?: RealtimeSearchRequest;
}

const localDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
};

const RealtimeSearchPanel: React.FC<RealtimeSearchPanelProps> = ({ fields, feeds, fixedFeedId, onSearch, onClear, searching, collapsed, onCollapsedChange, onFeedScopeChange, initialRequest }) => {
  const [query, setQuery] = useState(initialRequest?.query || '');
  const [conditions, setConditions] = useState<FilterCondition[]>(initialRequest?.conditions || []);
  const [feedId, setFeedId] = useState(initialRequest?.feed_ids?.[0] || '');
  const [alertLevel, setAlertLevel] = useState(initialRequest?.alert_levels?.[0] || '');
  const [fromAt, setFromAt] = useState(localDateTime(initialRequest?.from_at));
  const [toAt, setToAt] = useState(localDateTime(initialRequest?.to_at));
  const [matchingOnly, setMatchingOnly] = useState(initialRequest?.matching_only ?? true);
  const validation = conditions.length ? validateConditions(conditions, fields) : { ok: true, message: '' };
  const invalidDates = Boolean(fromAt && toAt && fromAt > toAt);
  const validationMessage = invalidDates ? 'Start time must be before end time.' : validation.ok ? '' : validation.message;
  const changeFeed = (value: string) => {
    setFeedId(value);
    setConditions([]);
    onFeedScopeChange?.(value);
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (searching || invalidDates || !validation.ok) return;
    const selectedFeed = fixedFeedId || feedId;
    const trimmedQuery = query.trim();
    onSearch({
      mode: trimmedQuery ? 'semantic' : 'conditions',
      ...(trimmedQuery ? { query: trimmedQuery } : {}),
      ...(conditions.length ? { conditions } : {}),
      ...(selectedFeed ? { feed_ids: [selectedFeed] } : {}),
      ...(alertLevel ? { alert_levels: [alertLevel as RealtimeAlertLevel] } : {}),
      ...(fromAt ? { from_at: new Date(fromAt).toISOString() } : {}),
      ...(toAt ? { to_at: new Date(toAt).toISOString() } : {}),
      matching_only: matchingOnly,
      page_size: 50,
    });
  };
  const clear = () => {
    setQuery(''); setConditions([]); setAlertLevel(''); setFromAt(''); setToAt(''); setMatchingOnly(true);
    if (!fixedFeedId) changeFeed('');
    onClear();
  };
  return <div className="realtime-search-panel">
    <SearchConfigurationPanel collapsed={collapsed} onCollapsedChange={onCollapsedChange}>
      <form className="search-form search-configuration-form" onSubmit={submit}>
        <SearchQueryInput
          label={fixedFeedId ? 'Search this feed' : 'Search project'}
          hint="Describe an observation or add filters"
          value={query}
          onChange={setQuery}
          placeholder="A person entering, a delivery, an event…"
          disabled={searching}
          maxLength={4000}
        />
        <div className="realtime-search-scope">
          {!fixedFeedId && <div className="form-group"><span className="form-label">Feed</span><CustomSelect value={feedId} options={[{ value: '', label: 'All feeds' }, ...feeds.map(feed => ({ value: feed.feed_id, label: feed.name }))]} onChange={changeFeed} disabled={searching} ariaLabel="Search feed" /></div>}
          <div className="form-group"><span className="form-label">Alert</span><CustomSelect value={alertLevel} options={[{ value: '', label: 'All alerts' }, ...REALTIME_ALERT_LEVELS.map(level => ({ value: level, label: REALTIME_ALERT_LABELS[level] }))]} onChange={setAlertLevel} disabled={searching} ariaLabel="Alert level" /></div>
        </div>
        <label className="realtime-search-matching"><input type="checkbox" checked={matchingOnly} onChange={event => setMatchingOnly(event.target.checked)} disabled={searching} /> Engine matches only</label>
        <details className="realtime-search-options">
          <summary>Time range</summary>
          <div className="realtime-search-time-range">
            <label className="form-group">From<input type="datetime-local" className="form-input" aria-label="From time" value={fromAt} onChange={event => setFromAt(event.target.value)} disabled={searching} /></label>
            <label className="form-group">To<input type="datetime-local" className="form-input" aria-label="To time" value={toAt} onChange={event => setToAt(event.target.value)} disabled={searching} /></label>
          </div>
        </details>
        <div className={`search-action-row ${conditions.length ? 'has-filters' : ''}`}>
          <button type="submit" className="search-button" disabled={searching || invalidDates || !validation.ok} title={validationMessage || undefined}><Search size={15} /><span>{searching ? 'Searching…' : 'Search'}</span></button>
          <button type="button" className="add-filter-btn clear-all-search-btn" onClick={clear} disabled={searching}><span>Clear Search</span></button>
          {conditions.length < MAX_CONDITIONS && <button type="button" className="add-filter-btn" aria-label="Add filter" title={fields.length ? 'Add filter' : 'No searchable fields in this scope'} disabled={searching || !fields.length} onClick={() => setConditions(current => [...current, { ...DEFAULT_CONDITION_FILTER }])}><Plus size={16} /><span>Filter</span></button>}
          <SearchConditionEditor filters={conditions} fieldDefinitions={fields} onChange={value => setConditions(normalizeConditionFilters(value, { availableFields: fields }))} disabled={searching} />
        </div>
        {validationMessage && <p className="realtime-search-validation" role="status">{validationMessage}</p>}
      </form>
    </SearchConfigurationPanel>
  </div>;
};

export default RealtimeSearchPanel;
